import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Trash2, Plus, CalendarDays, Images, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { LogoMark } from "@/components/Logo";

const STATUS = ["pendente", "confirmado", "concluido"];
const CATEGORIES = ["Blackwork", "Fine Line", "Tradicional", "Projetos Grandes"];

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("agendamentos");

  const [bookings, setBookings] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [content, setContent] = useState({ notice: "", hero_sub: "", services: [] });
  const [newItem, setNewItem] = useState({ url: "", title: "", category: "Blackwork", artist: "" });

  const loadAdminData = () => {
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent({ notice: r.data.notice || "", hero_sub: r.data.hero_sub || "", services: r.data.services || [] })).catch(() => {});
  };

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setUser(r.data); loadAdminData(); })
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("dt_token", data.token);
      setUser(data);
      loadAdminData();
      toast.success("Bem-vindo ao painel do estúdio");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Falha no login");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("dt_token");
    setUser(false);
  };

  const setBookingStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Status atualizado");
    } catch { toast.error("Erro ao atualizar status"); }
  };

  const addGalleryItem = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/gallery", newItem);
      setGallery((g) => [...g, data]);
      setNewItem({ url: "", title: "", category: "Blackwork", artist: "" });
      toast.success("Foto adicionada à galeria");
    } catch { toast.error("Erro ao adicionar foto"); }
  };

  const removeGalleryItem = async (id) => {
    try {
      await api.delete(`/admin/gallery/${id}`);
      setGallery((g) => g.filter((i) => i.id !== id));
      toast.success("Foto removida");
    } catch { toast.error("Erro ao remover foto"); }
  };

  const saveContent = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/content", content);
      toast.success("Conteúdo do site atualizado");
    } catch { toast.error("Erro ao salvar conteúdo"); }
  };

  const setService = (i, key, value) =>
    setContent((c) => ({ ...c, services: c.services.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)) }));

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-[#09090b]"><p className="font-mono-dt text-xs uppercase tracking-[0.3em] text-zinc-500">Carregando...</p></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-5" data-testid="admin-login-page">
        <motion.form onSubmit={login} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-sm rounded-lg border border-[#27272a] bg-[#121215] p-8" data-testid="admin-login-form">
          <div className="flex justify-center mb-6"><LogoMark size={52} /></div>
          <h1 className="font-display font-bold text-xl text-center">Área do Estúdio</h1>
          <p className="font-mono-dt text-[10px] uppercase tracking-[0.25em] text-zinc-500 text-center mt-2 mb-7">Acesso restrito</p>
          <input type="email" required placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)}
            className="dt-input mb-4" data-testid="admin-login-email" />
          <input type="password" required placeholder="Senha" value={password} onChange={(e) => setPassword(e.target.value)}
            className="dt-input mb-4" data-testid="admin-login-password" />
          {error && <p className="text-xs text-[#e03131] mb-4" data-testid="admin-login-error">{error}</p>}
          <button type="submit" data-testid="admin-login-button"
            className="w-full py-3.5 rounded-md bg-[#e03131] text-white font-mono-dt text-xs uppercase tracking-[0.2em] hover:bg-[#c92a2a] transition-colors">
            Entrar
          </button>
          <Link to="/" className="mt-5 flex items-center justify-center gap-2 text-xs text-zinc-500 hover:text-[#e2d9cc] transition-colors" data-testid="admin-back-home-link">
            <ArrowLeft size={13} /> Voltar ao site
          </Link>
        </motion.form>
      </div>
    );
  }

  const TABS = [
    { id: "agendamentos", label: "Agendamentos", icon: CalendarDays },
    { id: "galeria", label: "Galeria", icon: Images },
    { id: "conteudo", label: "Conteúdo", icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="admin-panel">
      <header className="border-b border-[#27272a] bg-[#0d0d10]">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={34} />
            <div>
              <p className="font-display font-bold text-sm">Painel do Estúdio</p>
              <p className="font-mono-dt text-[10px] text-zinc-500 tracking-[0.2em] uppercase">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-zinc-400 hover:text-[#e2d9cc] transition-colors flex items-center gap-2" data-testid="admin-view-site-link">
              <ArrowLeft size={14} /> Ver site
            </Link>
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-2 px-4 py-2 rounded-md border border-[#27272a] text-xs text-zinc-300 hover:border-[#e03131] hover:text-[#e03131] transition-colors">
              <LogOut size={13} /> Sair
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex gap-2 mb-8 flex-wrap" data-testid="admin-tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-md font-mono-dt text-xs uppercase tracking-[0.15em] border transition-colors ${
                tab === id ? "bg-[#e03131] border-[#e03131] text-white" : "border-[#27272a] text-zinc-400 hover:text-[#e2d9cc]"
              }`}>
              <Icon size={14} />{label}
            </button>
          ))}
        </div>

        {tab === "agendamentos" && (
          <div className="space-y-4" data-testid="admin-bookings-list">
            {bookings.length === 0 && <p className="text-sm text-zinc-500 py-10 text-center font-mono-dt uppercase tracking-[0.2em]">Nenhum agendamento recebido ainda</p>}
            {bookings.map((b) => (
              <div key={b.id} className="rounded-lg border border-[#27272a] bg-[#121215] p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between" data-testid={`admin-booking-${b.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display font-semibold">{b.name}</h3>
                    <span className="font-mono-dt text-[10px] uppercase tracking-[0.2em] text-[#e03131]">{b.style}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1.5">{b.phone} · {b.email}</p>
                  <p className="text-xs text-zinc-400 mt-2">
                    {[b.body_part, b.size, b.budget, b.artist, b.date_pref && `${b.date_pref} (${b.shift})`].filter(Boolean).join(" · ")}
                  </p>
                  {b.description && <p className="text-xs text-zinc-500 mt-2 italic">"{b.description}"</p>}
                </div>
                <select value={b.status} onChange={(e) => setBookingStatus(b.id, e.target.value)} data-testid={`admin-booking-status-${b.id}`}
                  className="dt-input !w-auto shrink-0 capitalize">
                  {STATUS.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}

        {tab === "galeria" && (
          <div data-testid="admin-gallery-manager">
            <form onSubmit={addGalleryItem} className="rounded-lg border border-[#27272a] bg-[#121215] p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-testid="admin-gallery-form">
              <input required placeholder="URL da imagem" value={newItem.url} onChange={(e) => setNewItem({ ...newItem, url: e.target.value })}
                className="dt-input sm:col-span-2" data-testid="admin-gallery-input-url" />
              <input required placeholder="Título" value={newItem.title} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                className="dt-input" data-testid="admin-gallery-input-title" />
              <input placeholder="Artista" value={newItem.artist} onChange={(e) => setNewItem({ ...newItem, artist: e.target.value })}
                className="dt-input" data-testid="admin-gallery-input-artist" />
              <select value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                className="dt-input" data-testid="admin-gallery-select-category">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <button type="submit" data-testid="admin-gallery-add-button"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#e03131] text-white font-mono-dt text-xs uppercase tracking-[0.15em] hover:bg-[#c92a2a] transition-colors">
                <Plus size={14} /> Adicionar foto
              </button>
            </form>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.map((g) => (
                <div key={g.id} className="group relative rounded-lg overflow-hidden border border-[#27272a]" data-testid={`admin-gallery-item-${g.id}`}>
                  <img src={g.url} alt={g.title} className="w-full h-40 object-cover" />
                  <div className="absolute inset-0 bg-[#09090b]/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-3">
                    <p className="text-xs text-center text-[#e2d9cc]">{g.title}</p>
                    <p className="font-mono-dt text-[9px] uppercase tracking-[0.2em] text-[#e03131]">{g.category}</p>
                    <button onClick={() => removeGalleryItem(g.id)} data-testid={`admin-gallery-delete-${g.id}`}
                      className="mt-1 flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#e03131]/60 text-[#e03131] text-[10px] font-mono-dt uppercase tracking-[0.15em] hover:bg-[#e03131] hover:text-white transition-colors">
                      <Trash2 size={11} /> Remover
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "conteudo" && (
          <form onSubmit={saveContent} className="space-y-6 max-w-3xl" data-testid="admin-content-form">
            <div className="rounded-lg border border-[#27272a] bg-[#121215] p-6 space-y-4">
              <h3 className="font-display font-semibold">Textos do site</h3>
              <div>
                <span className="block font-mono-dt text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Aviso do estúdio (seção Serviços)</span>
                <textarea rows={2} value={content.notice} onChange={(e) => setContent({ ...content, notice: e.target.value })}
                  className="dt-input resize-none" data-testid="admin-content-notice" />
              </div>
              <div>
                <span className="block font-mono-dt text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Subtítulo do Hero</span>
                <textarea rows={2} value={content.hero_sub} onChange={(e) => setContent({ ...content, hero_sub: e.target.value })}
                  className="dt-input resize-none" data-testid="admin-content-hero-sub" />
              </div>
            </div>
            <div className="rounded-lg border border-[#27272a] bg-[#121215] p-6 space-y-5">
              <h3 className="font-display font-semibold">Serviços & Preços</h3>
              {content.services.map((s, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-5 border-b border-[#27272a]/60 last:border-0 last:pb-0" data-testid={`admin-service-row-${i}`}>
                  <input value={s.title} onChange={(e) => setService(i, "title", e.target.value)} className="dt-input" placeholder="Título" data-testid={`admin-service-title-${i}`} />
                  <input value={s.price} onChange={(e) => setService(i, "price", e.target.value)} className="dt-input" placeholder="Preço" data-testid={`admin-service-price-${i}`} />
                  <input value={s.details} onChange={(e) => setService(i, "details", e.target.value)} className="dt-input sm:col-span-2" placeholder="Detalhes" data-testid={`admin-service-details-${i}`} />
                </div>
              ))}
            </div>
            <button type="submit" data-testid="admin-content-save-button"
              className="px-7 py-3.5 rounded-md bg-[#e03131] text-white font-mono-dt text-xs uppercase tracking-[0.2em] hover:bg-[#c92a2a] transition-colors">
              Salvar alterações
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
